import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mail, Phone, MapPin, Calendar, User, MessageSquare, Eye, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ logout, sessionExpired }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Set page title
    document.title = 'Admin - FATT Elevadores';
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact`, { withCredentials: true });
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setContacts([]);
        sessionExpired();
        return;
      }
      console.error('Erro ao buscar contatos:', error);
      setError('Erro ao carregar contatos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'new':
        return 'Novo';
      case 'contacted':
        return 'Contatado';
      case 'completed':
        return 'Finalizado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-20">
            <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600" data-testid="admin-contacts-loading">Carregando contatos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800" data-testid="admin-panel-title">Painel Administrativo</h1>
              <p className="text-slate-600 mt-2">FATT Elevadores - Contatos Recebidos</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-slate-800" data-testid="admin-contact-count">{contacts.length}</div>
              <div className="text-slate-600">Total de Contatos</div>
              <Button className="mt-3" variant="outline" data-testid="admin-logout" onClick={async () => {
                try { await logout(); } catch { setError('Não foi possível encerrar a sessão. Tente novamente.'); }
              }}>Sair</Button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-white">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-red-100 rounded-full">
                <Eye className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800" data-testid="admin-new-count">
                  {contacts.filter(c => c.status === 'new').length}
                </div>
                <div className="text-slate-600 text-sm">Novos</div>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-white">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Phone className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800" data-testid="admin-contacted-count">
                  {contacts.filter(c => c.status === 'contacted').length}
                </div>
                <div className="text-slate-600 text-sm">Contatados</div>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-white">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800" data-testid="admin-completed-count">
                  {contacts.filter(c => c.status === 'completed').length}
                </div>
                <div className="text-slate-600 text-sm">Finalizados</div>
              </div>
            </div>
          </Card>
          
          <Card className="p-6 bg-white">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-full">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800" data-testid="admin-today-count">
                  {contacts.filter(c => {
                    const today = new Date();
                    const contactDate = new Date(c.created_at);
                    return contactDate.toDateString() === today.toDateString();
                  }).length}
                </div>
                <div className="text-slate-600 text-sm">Hoje</div>
              </div>
            </div>
          </Card>
        </div>

        {error && (
          <Card className="p-4 mb-6 bg-red-50 border-red-200">
            <p className="text-red-700" role="alert" data-testid="admin-contact-error">{error}</p>
            <Button 
              onClick={fetchContacts}
              data-testid="admin-retry-contacts"
              className="mt-2 bg-red-600 hover:bg-red-700 text-white"
            >
              Tentar Novamente
            </Button>
          </Card>
        )}

        {/* Contacts List */}
        <div className="space-y-6">
          {contacts.length === 0 ? (
            <Card className="p-12 text-center bg-white">
              <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 text-lg" data-testid="admin-no-contacts">Nenhum contato recebido ainda.</p>
              <p className="text-slate-500">Os contatos enviados pelo formulário aparecerão aqui.</p>
            </Card>
          ) : (
            contacts.map((contact, index) => (
              <Card key={contact.id} data-testid={`admin-contact-${contact.id}`} className="p-6 bg-white hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  {/* Contact Info */}
                  <div className="flex-1 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-slate-100 rounded-full">
                          <User className="w-5 h-5 text-slate-700" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-800" data-testid={`admin-contact-name-${contact.id}`}>{contact.name}</h3>
                          <div className="flex items-center space-x-2 text-sm text-slate-500">
                            <Calendar className="w-4 h-4" />
                            <span data-testid={`admin-contact-date-${contact.id}`}>{formatDate(contact.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <Badge className={`${getStatusColor(contact.status)} border`} data-testid={`admin-contact-status-${contact.id}`}>
                        {getStatusText(contact.status)}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-4 h-4 text-slate-500" />
                        <a href={`mailto:${contact.email}`} data-testid={`admin-contact-email-${contact.id}`} className="text-slate-700 hover:text-slate-900 hover:underline">
                          {contact.email}
                        </a>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Phone className="w-4 h-4 text-slate-500" />
                        <a href={`tel:${contact.phone}`} data-testid={`admin-contact-phone-${contact.id}`} className="text-slate-700 hover:text-slate-900 hover:underline">
                          {contact.phone}
                        </a>
                      </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <MessageSquare className="w-4 h-4 text-slate-500 mt-1" />
                        <div>
                          <p className="text-sm font-medium text-slate-700 mb-1">Mensagem:</p>
                          <p className="text-slate-600 leading-relaxed" data-testid={`admin-contact-message-${contact.id}`}>{contact.message}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="lg:ml-6 flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2">
                    <Button 
                      size="sm" 
                      data-testid={`admin-contact-email-action-${contact.id}`}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => window.open(`mailto:${contact.email}?subject=FATT Elevadores - Resposta ao seu contato`, '_blank')}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      E-mail
                    </Button>
                    <Button 
                      size="sm" 
                      data-testid={`admin-contact-whatsapp-action-${contact.id}`}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => window.open(`https://wa.me/${contact.phone.replace(/\D/g, '')}?text=Olá ${contact.name}, sou da FATT Elevadores e recebi seu contato pelo site.`, '_blank')}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;